import { useState } from 'react'
import {
  Stack, Paper, Group, Text, ActionIcon, Button,
  FileButton, Progress, Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconPaperclip, IconDownload, IconUpload,
  IconFile, IconTrash, IconAlertCircle, IconFileOff,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

interface Attachment {
  id: number
  filename: string
  mime_type: string
  size_bytes: number
  uploader_name: string | null
  created_at: string
}

interface PresignResponse {
  presigned_put_url: string
  s3_key: string
}

interface Props {
  taskRequestId: number
  tenantId: number
  canUpload?: boolean
}

function humanizeSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function AttachmentList({ taskRequestId, tenantId, canUpload }: Props) {
  const queryClient = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const basePath = `/v1/tenants/${tenantId}/workflow/${taskRequestId}/attachments`

  const { data: attachments = [], isLoading, error } = useQuery<Attachment[]>({
    queryKey: ['attachments', tenantId, taskRequestId],
    queryFn: async () => {
      const res = await api.get<Attachment[]>(basePath)
      return res.data
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const mimeType = file.type || 'application/octet-stream'

      // Step 1: get presigned PUT URL + s3_key
      setUploadProgress(10)
      const presignRes = await api.post<PresignResponse>(`${basePath}/presign`, {
        filename: file.name,
        mime_type: mimeType,
        size_bytes: file.size,
      })
      const { presigned_put_url, s3_key } = presignRes.data

      // Step 2: upload to S3
      setUploadProgress(30)
      await fetch(presigned_put_url, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      })
      setUploadProgress(80)

      // Step 3: confirm upload with file metadata
      await api.post(basePath, {
        s3_key,
        filename: file.name,
        mime_type: mimeType,
        size_bytes: file.size,
      })
      setUploadProgress(100)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', tenantId, taskRequestId] })
      notifications.show({ color: 'green', message: 'Arquivo enviado com sucesso!' })
      setUploadProgress(null)
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro no upload: ${err.message}` })
      setUploadProgress(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`${basePath}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', tenantId, taskRequestId] })
      notifications.show({ color: 'green', message: 'Evidência removida.' })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro: ${err.message}` })
    },
  })

  const handleDownload = async (att: Attachment) => {
    try {
      const res = await api.get<{ url: string }>(`${basePath}/${att.id}`)
      window.open(res.data.url, '_blank')
    } catch {
      notifications.show({ color: 'red', message: 'Erro ao gerar link de download.' })
    }
  }

  const handleDelete = (att: Attachment) => {
    if (window.confirm(`Remover "${att.filename}"?`)) {
      deleteMutation.mutate(att.id)
    }
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group gap="xs">
          <IconPaperclip size={16} />
          <Text size="sm" fw={600}>
            Evidências {attachments.length > 0 && `(${attachments.length})`}
          </Text>
        </Group>
        {canUpload && (
          <FileButton onChange={(file) => file && uploadMutation.mutate(file)}>
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                leftSection={<IconUpload size={14} />}
                loading={uploadMutation.isPending}
              >
                Enviar arquivo
              </Button>
            )}
          </FileButton>
        )}
      </Group>

      {uploadProgress !== null && (
        <Progress value={uploadProgress} size="sm" animated role="status" aria-label="Progresso do upload" />
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" py="xs">
          Não foi possível carregar as evidências. Tente novamente.
        </Alert>
      )}

      {isLoading && <Text size="sm" c="dimmed" role="status">Carregando evidências...</Text>}

      {!isLoading && attachments.length === 0 && (
        <Stack align="center" gap="xs" py="md">
          <IconFileOff size={32} color="var(--mantine-color-dimmed)" stroke={1.5} />
          <Text size="sm" c="dimmed">Nenhuma evidência adicionada ainda.</Text>
        </Stack>
      )}

      {attachments.map((att) => (
        <Paper key={att.id} p="xs" withBorder>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <IconFile size={18} style={{ flexShrink: 0 }} />
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Text size="sm" fw={500} truncate>{att.filename}</Text>
                <Text size="xs" c="dimmed">
                  {humanizeSize(att.size_bytes)}
                  {att.uploader_name && ` — ${att.uploader_name}`}
                  {` — ${formatDate(att.created_at)}`}
                </Text>
              </Stack>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color="blue"
                size="sm"
                onClick={() => handleDownload(att)}
                title="Baixar"
                aria-label={`Baixar ${att.filename}`}
              >
                <IconDownload size={14} />
              </ActionIcon>
              {canUpload && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={() => handleDelete(att)}
                  loading={deleteMutation.isPending}
                  title="Remover"
                  aria-label={`Remover ${att.filename}`}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Group>
          </Group>
        </Paper>
      ))}
    </Stack>
  )
}
